/**
 * i18next set-up — every user-facing string the frontend writes itself lives in
 * `locales/<language>.json` and is looked up through `t()`.
 *
 * English is the default and the fallback, and the only language in the main
 * bundle. Every other language — its resource file, its education text and its
 * how-to guides — is one chunk of its own, fetched when that language is chosen
 * (the switch, signup, Settings, the browser's language or a signed-in user's) and
 * then kept by the service worker like the rest of the app. Rendering waits for it:
 * main.tsx holds the first render until the starting language is in
 * (`languageReady`), and i18next changes language only once the new one has loaded,
 * so English never shows in its place.
 *
 * A language other than English is OFFERED — shown on the language switch, the
 * signup field and Settings — only when its file is complete
 * (lib/localeCompleteness.ts) and so is its long-form content: the how-to guides
 * and the education entries. That is decided at build time from the files
 * (lib/languageGate.ts, through `virtual:language-gate`). Adding
 * `locales/pt-BR.json`, `content/how-tos/pt-BR/` with its titles, and
 * `content/education/pt-BR.json` is what turned Brazilian Portuguese on; Spanish
 * ('es') waits on the same files. I18N-NEW-LANGUAGE.md lists every one.
 *
 * Conventions (enforced by src/__tests__/i18nKeys.test.ts):
 *  - Keys are grouped by screen or feature (`dailyLog.saveButton`) and are always
 *    string literals at the call site, so every key in use can be found by search.
 *  - A sentence is one key. Values go in as interpolation (`{{catName}}`), counts
 *    as i18next plurals (`_one` / `_other`), and a cat's sex as context
 *    (`_male` / `_female`, base key for unknown) — never as sentence fragments.
 *    Where a sentence names the cat and the cat may not have a name yet, the
 *    nameless sentence is its own key (`…NoName`), never "your cat" passed in.
 *
 * The clinical engine's text is here too, under `engine`: the engine sends a code
 * and params, and lib/engineMessage.ts renders `engine.<code>`. That section is a
 * copy of the engine's own catalogue (packages/clinical-engine/src/locales/en.json)
 * and a test keeps the two identical. Text the backend composes is not in here.
 */
import i18n, { type BackendModule, type ResourceKey } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { OFFERED_LANGUAGES } from 'virtual:language-gate';
import en from './locales/en.json';
import { capitalizeFirst } from './lib/pronouns';
import { loadEducationText } from './lib/educationText';
import { loadHowToContent } from './lib/howToContent';
import {
  DEFAULT_LANGUAGE,
  cachedUserLanguage,
  isLanguage,
  languageForLocale,
  storedPreAccountLanguage,
  type Language,
} from './lib/language';

export { DEFAULT_LANGUAGE };

type Resource = Record<string, unknown>;

/** Every other language's resource file, fetched on demand. */
const LAZY_FILES = import.meta.glob<Resource>(['./locales/*.json', '!./locales/en.json'], { import: 'default' });

/** The languages with a complete resource file and complete content, English first. */
const OFFERED: readonly Language[] = OFFERED_LANGUAGES;

export function offeredLanguages(): readonly Language[] {
  return OFFERED;
}

/** True when there is more than one language to choose from. */
export function hasLanguageChoice(): boolean {
  return OFFERED.length > 1;
}

/** `lang` if it is offered, otherwise English. */
export function offeredOrDefault(lang: string | null | undefined): Language {
  return (OFFERED as readonly string[]).includes(lang ?? '') ? (lang as Language) : DEFAULT_LANGUAGE;
}

/** The language for pages shown before login: the switch's choice, else the browser's. */
export function preAccountLanguage(): Language {
  const browser = typeof navigator !== 'undefined' ? navigator.language : undefined;
  return offeredOrDefault(storedPreAccountLanguage() ?? languageForLocale(browser));
}

/**
 * Show the app in `lang` (English if it is not offered). Screens re-render once the
 * language has loaded; see App.tsx. Resolves then. If it cannot be fetched (offline,
 * and never fetched before), the app stays in the language it is in.
 */
export async function applyLanguage(lang: string | null | undefined): Promise<void> {
  const next = offeredOrDefault(lang);
  if (i18n.language === next) return;
  await i18n.loadLanguages(next).catch(() => undefined);
  if (i18n.hasResourceBundle(next, 'translation')) await i18n.changeLanguage(next);
}

/** Fetch every offered language — for the signup field, which names itself in each. */
export function loadOfferedLanguages(): Promise<void> {
  return i18n.loadLanguages([...OFFERED]).catch(() => undefined);
}

/** True when every offered language is loaded. */
export function offeredLanguagesLoaded(): boolean {
  return OFFERED.every((lang) => i18n.hasResourceBundle(lang, 'translation'));
}

/**
 * Fetches a language: its resource file, and with it its education text and how-to
 * guides, so the whole language is in when i18next switches to it. English is
 * bundled and never fetched.
 */
const languageChunks: BackendModule = {
  type: 'backend',
  init: () => undefined,
  read(language, _namespace, callback) {
    const load = isLanguage(language) ? LAZY_FILES[`./locales/${language}.json`] : undefined;
    if (!load) {
      callback(null, {});
      return;
    }
    Promise.all([load(), loadEducationText(language as Language), loadHowToContent(language as Language)]).then(
      ([resource]) => callback(null, resource as ResourceKey),
      (error: Error) => callback(error, false),
    );
  },
};

function syncDocumentLanguage(lng: string): void {
  if (typeof document !== 'undefined') document.documentElement.lang = lng;
}

i18n.on('languageChanged', syncDocumentLanguage);

/** Resolves once the starting language is loaded (at once for English). main.tsx waits on it. */
export const languageReady: Promise<void> = new Promise((resolve) => {
  void i18n
    .use(languageChunks)
    .use(initReactI18next)
    .init(
      {
        // English is bundled; the rest come from `languageChunks`.
        resources: { [DEFAULT_LANGUAGE]: { translation: en } },
        partialBundledLanguages: true,
        // A restored session's language, else the pre-login choice. The auth store
        // re-applies the user's language once it has the user (stores/authStore.ts).
        lng: offeredOrDefault(cachedUserLanguage() ?? preAccountLanguage()),
        fallbackLng: DEFAULT_LANGUAGE,
        // English is bundled, so initialise synchronously: `t()` works at import time.
        initAsync: false,
        // React escapes rendered text already.
        interpolation: { escapeValue: false },
        returnNull: false,
      },
      () => resolve(),
    );
});

syncDocumentLanguage(i18n.language);

// `{{catName, capitalize}}` — where a name opens a sentence.
i18n.services.formatter?.add('capitalize', (value: unknown) => capitalizeFirst(String(value ?? '')));

export default i18n;
